import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from 'react-query';
import styled from 'styled-components';
import { Spinner } from 'Components/common/Spinner';
import { useTranslation } from 'react-i18next';
import { LOCALE_MAP } from 'utils/locale-formatting';
import { getVendors, deleteVendor } from 'services/vendor-service';
import { formatDateTimeToDateTimeBR, formatDateToDateBR } from 'utils/date_utils';
import { Dialog, Select, TextField } from 'Components/ui';
import { useQueryClient } from 'react-query';

const INK = 'var(--vo-ink)';
const MUTED = 'var(--vo-slate)';
const BLUE = 'var(--vo-celeste)';
const TABLE_BG = 'var(--vo-mist)';
const ROW_HOVER = 'var(--vo-capri-25)';

const BADGE_BG = {
  error:   'var(--vo-danger-10)',
  warning: 'var(--vo-warning-10)',
  success: 'var(--vo-success-10)',
  default: 'var(--vo-slate-10)',
};
const BADGE_COLOR = {
  error:   'var(--vo-danger)',
  warning: 'var(--vo-warning)',
  success: 'var(--vo-success)',
  default: 'var(--vo-slate)',
};

const Wrapper = styled.div``;

const InfoBanner = styled.div`
  background: white;
  border-left: 4px solid ${BLUE};
  border-radius: 14px;
  padding: 18px 24px;
  box-shadow: 0 1px 4px var(--vo-escuro-8);
  display: flex;
  align-items: flex-start;
  gap: 16px;
  margin-bottom: 20px;
`;

const BannerTitle = styled.strong`
  font-family: 'Rubik', sans-serif;
  font-size: 15px;
  color: ${INK};
  display: block;
`;
const BannerDesc = styled.p`
  margin: 4px 0 0;
  font-family: 'Lato', sans-serif;
  font-size: 13px;
  color: ${MUTED};
`;

const FilterCard = styled.div`
  background: white;
  border-radius: 14px;
  padding: 20px 24px;
  box-shadow: 0 1px 4px var(--vo-escuro-8);
  margin-bottom: 20px;
`;
const FilterRow = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 12px;
`;
const FilterGroup = styled.div`
  flex: ${({ $width }) => $width ? `0 0 ${$width}` : '1'};
`;
const FieldLabel = styled.label`
  display: block;
  font-family: 'Lato', sans-serif;
  font-size: 12px;
  color: ${MUTED};
  margin-bottom: 6px;
`;
const SearchWrapper = styled.div`
  position: relative;
`;
const SearchIconWrapper = styled.span`
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: var(--vo-steel);
  display: flex;
  pointer-events: none;
`;
const FilterBtn = styled.button`
  height: 40px;
  padding: 0 20px;
  background: ${BLUE};
  color: white;
  border: none;
  border-radius: 10px;
  font-family: 'Lato', sans-serif;
  font-size: 13px;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  white-space: nowrap;
  flex-shrink: 0;

  &:hover {
    background: color-mix(in srgb, var(--vo-celeste) 80%, black);
  }
`;

const TableCard = styled.div`
  background: white;
  border-radius: 14px;
  box-shadow: 0 1px 4px var(--vo-escuro-8);
  overflow: hidden;
`;
const StyledTable = styled.table`
  width: 100%;
  border-collapse: collapse;
`;
const StyledThead = styled.thead`
  background: ${TABLE_BG};
`;
const StyledTh = styled.th`
  padding: 12px 16px;
  text-align: left;
  font-family: 'Maven Pro', sans-serif;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: .08em;
  color: ${MUTED};
  white-space: nowrap;
  cursor: ${({ $sortable }) => $sortable ? 'pointer' : 'default'};
  user-select: none;
`;
const StyledThCenter = styled(StyledTh)`
  text-align: center;
`;
const SortIndicator = styled.span`
  margin-left: 4px;
  font-size: 10px;
`;
const StyledTbody = styled.tbody``;
const StyledTr = styled.tr`
  &:hover {
    background: ${ROW_HOVER};
  }
  &:not(:last-child) {
    border-bottom: 1px solid ${TABLE_BG};
  }
`;
const StyledTd = styled.td`
  padding: 12px 16px;
  font-size: 14px;
  color: ${INK};
  vertical-align: middle;
`;
const StyledTdCenter = styled(StyledTd)`
  text-align: center;
`;
const VendorNameCell = styled.div`
  font-family: 'Maven Pro', sans-serif;
  font-size: 14px;
  font-weight: 600;
  color: ${INK};
`;
const VendorEmailCell = styled.div`
  font-size: 12px;
  color: ${MUTED};
  margin-top: 2px;
`;
const TierChip = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 3px 10px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  line-height: 20px;
  white-space: nowrap;
  background-color: ${({ $color }) => BADGE_BG[$color] || BADGE_BG.default};
  color: ${({ $color }) => BADGE_COLOR[$color] || BADGE_COLOR.default};
`;
const MutedText = styled.span`
  font-size: 12px;
  color: ${MUTED};
`;
const ActionCell = styled.div`
  display: flex;
  justify-content: center;
  gap: 4px;
`;
const ActionIconBtn = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  padding: 0;
  border: none;
  border-radius: 8px;
  background: transparent;
  cursor: pointer;
  color: ${({ $variant }) => $variant === 'error' ? 'var(--vo-danger)' : MUTED};

  &:hover {
    background: ${({ $variant }) => $variant === 'error' ? 'var(--vo-danger-10)' : 'var(--vo-info-10)'};
    color: ${({ $variant }) => $variant === 'error' ? 'var(--vo-danger)' : BLUE};
  }
`;
const PaginationRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 20px;
  border-top: 1px solid ${TABLE_BG};
`;
const CountText = styled.div`
  font-family: 'Lato', sans-serif;
  font-size: 13px;
  color: ${MUTED};
`;
const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  padding: 32px;
`;
const ErrorBox = styled.div`
  padding: 32px;
  color: var(--vo-danger);
  font-size: 14px;
`;
const EmptyCell = styled.td`
  text-align: center;
  padding: 32px;
  color: ${MUTED};
  font-size: 14px;
`;

const SearchSVG = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
  </svg>
);
const VisibilitySVG = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" />
  </svg>
);
const EditSVG = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" />
    <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" />
  </svg>
);
const DeleteSVG = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2" />
  </svg>
);


export const VendorList = ({ onEdit, tenant }) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { t, i18n } = useTranslation(['vendor', 'common']);

  const [orderBy, setOrderBy] = useState('name');
  const [order, setOrder] = useState('asc');
  const [pendingSearch, setPendingSearch] = useState('');
  const [pendingTier, setPendingTier] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [tierFilter, setTierFilter] = useState('');

  const applyFilters = () => {
    setSearchTerm(pendingSearch);
    setTierFilter(pendingTier);
  };

  const { data, isLoading, error } = useQuery(
    ['vendors', tenant, tierFilter, searchTerm],
    () => getVendors(tenant, undefined, undefined, {
      tier: (tierFilter && tierFilter !== 'all') ? tierFilter : undefined,
      search: searchTerm || undefined,
      active: true
    }),
    {
      enabled: !!tenant,
      keepPreviousData: true
    }
  );

  const vendors = data?.data?.data || [];
  const total = data?.data?.pagination?.total || 0;

  const handleSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleDelete = async (vendorId, vendorName) => {
    const confirmed = await Dialog.confirm({
      title: t('vendor:notifications.deleteTitle'),
      message: t('vendor:notifications.deleteText', { name: vendorName }),
      confirmColor: 'var(--vo-danger)',
      confirmLabel: t('vendor:notifications.deleteConfirm'),
      cancelLabel: t('common:buttons.cancel')
    });

    if (confirmed) {
      try {
        await deleteVendor(vendorId);
        queryClient.invalidateQueries('vendors');
        Dialog.fire({
          icon: 'success',
          title: t('vendor:notifications.deleteSuccess'),
          text: t('vendor:notifications.deleteSuccessText'),
          timer: 2000,
          showConfirmButton: false
        });
      } catch (error) {
        Dialog.alert({
          icon: 'error',
          title: t('vendor:notifications.errorTitle'),
          message: error.response?.data?.message || t('vendor:notifications.deleteError')
        });
      }
    }
  };

  const getTierBadge = (tier) => {
    const badges = {
      1: { label: t('vendor:tier.1.label'), color: 'error' },
      2: { label: t('vendor:tier.2.label'), color: 'warning' },
      3: { label: t('vendor:tier.3.label'), color: 'success' }
    };
    return badges[tier] || { label: t('vendor:tier.unknown.label'), color: 'default' };
  };

  const getTrustScoreColor = (score) => {
    if (!score) return 'default';
    if (score >= 90) return 'success';
    if (score >= 70) return 'warning';
    return 'error';
  };

  const sortedVendors = React.useMemo(() => {
    const locale = LOCALE_MAP[i18n.language] || 'pt-BR';
    const collator = new Intl.Collator(locale, { sensitivity: 'base' });
    return [...vendors].sort((a, b) => {
      let aValue = a[orderBy];
      let bValue = b[orderBy];
      if (orderBy === 'lastAssessmentDate') {
        aValue = a.lastAssessment?.date || 0;
        bValue = b.lastAssessment?.date || 0;
      }
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        return order === 'asc' ? collator.compare(aValue, bValue) : collator.compare(bValue, aValue);
      }
      if (aValue === bValue) return 0;
      return order === 'asc' ? (aValue > bValue ? 1 : -1) : (aValue < bValue ? 1 : -1);
    });
  }, [vendors, orderBy, order, i18n.language]);

  if (isLoading) {
    return (
      <LoadingContainer>
        <Spinner $size={24} $borderColor="var(--vo-mist)" $speed="0.6s" />
      </LoadingContainer>
    );
  }

  if (error) {
    return (
      <ErrorBox>{t('common:errors.generic')}: {error.message}</ErrorBox>
    );
  }

  return (
    <Wrapper>
      <InfoBanner>
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={BLUE} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, marginTop: 1 }}>
          <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
        </svg>
        <div>
          <BannerTitle>{t('vendor:list.banner.title')}</BannerTitle>
          <BannerDesc>{t('vendor:list.banner.description')}</BannerDesc>
        </div>
      </InfoBanner>

      <FilterCard>
        <FilterRow>
          <FilterGroup>
            <FieldLabel>{t('vendor:table.filters.searchLabel')}</FieldLabel>
            <SearchWrapper>
              <TextField
                fullWidth
                placeholder={t('vendor:table.filters.searchPlaceholder')}
                value={pendingSearch}
                onChange={(e) => setPendingSearch(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && applyFilters()}
                sx={{ '& .MuiOutlinedInput-input.MuiOutlinedInput-input': { paddingRight: '40px' } }}
              />
              <SearchIconWrapper>
                <SearchSVG />
              </SearchIconWrapper>
            </SearchWrapper>
          </FilterGroup>

          <FilterGroup $flex="0" $width="170px">
            <FieldLabel>{t('vendor:table.filters.tierLabel')}</FieldLabel>
            <Select
              name="tierFilter"
              value={pendingTier}
              onChange={setPendingTier}
              options={[
                { value: '', label: t('vendor:table.filters.selectTier') },
                { value: 'all', label: t('vendor:table.filters.allTiers') },
                { value: '1', label: t('vendor:table.filters.tier1') },
                { value: '2', label: t('vendor:table.filters.tier2') },
                { value: '3', label: t('vendor:table.filters.tier3') },
              ]}
            />
          </FilterGroup>

          <FilterBtn onClick={applyFilters}>
            <SearchSVG />
            {t('vendor:table.filters.filterButton')}
          </FilterBtn>
        </FilterRow>
      </FilterCard>

      <TableCard>
        <StyledTable>
          <StyledThead>
            <tr>
              <StyledTh $sortable onClick={() => handleSort('name')}>
                {t('vendor:table.columns.name')}
                {orderBy === 'name' && <SortIndicator>{order === 'asc' ? '▲' : '▼'}</SortIndicator>}
              </StyledTh>
              <StyledTh $sortable onClick={() => handleSort('tier')}>
                {t('vendor:table.columns.tier')}
                {orderBy === 'tier' && <SortIndicator>{order === 'asc' ? '▲' : '▼'}</SortIndicator>}
              </StyledTh>
              <StyledTh>{t('vendor:table.columns.trustScore')}</StyledTh>
              <StyledTh $sortable onClick={() => handleSort('lastAssessmentDate')}>
                {t('vendor:table.columns.lastAssessment')}
                {orderBy === 'lastAssessmentDate' && <SortIndicator>{order === 'asc' ? '▲' : '▼'}</SortIndicator>}
              </StyledTh>
              <StyledTh>{t('vendor:table.columns.dpa')}</StyledTh>
              <StyledTh>{t('vendor:table.columns.recertification')}</StyledTh>
              <StyledThCenter>{t('common:table.actions')}</StyledThCenter>
            </tr>
          </StyledThead>
          <StyledTbody>
            {sortedVendors.length === 0 ? (
              <tr>
                <EmptyCell colSpan={7}>{t('vendor:list.emptyState')}</EmptyCell>
              </tr>
            ) : (
              sortedVendors.map((vendor) => {
                const tierBadge = getTierBadge(vendor.tier);
                const trustScore = vendor.lastAssessment?.trustScore;

                return (
                  <StyledTr key={vendor._id}>
                    <StyledTd>
                      <VendorNameCell>{vendor.name}</VendorNameCell>
                      <VendorEmailCell>{vendor.contactEmail}</VendorEmailCell>
                    </StyledTd>
                    <StyledTd>
                      <TierChip $color={tierBadge.color}>{tierBadge.label}</TierChip>
                    </StyledTd>
                    <StyledTd>
                      {trustScore ? (
                        <TierChip $color={getTrustScoreColor(trustScore)}>{trustScore}%</TierChip>
                      ) : (
                        <MutedText>{t('vendor:table.noAssessment')}</MutedText>
                      )}
                    </StyledTd>
                    <StyledTd>
                      {vendor.lastAssessment?.date ? (
                        formatDateTimeToDateTimeBR(vendor.lastAssessment.date)
                      ) : (
                        <MutedText>{t('vendor:table.never')}</MutedText>
                      )}
                    </StyledTd>
                    <StyledTd>
                      <TierChip $color={vendor.dpaEstablished ? 'success' : 'default'}>
                        {vendor.dpaEstablished ? t('common:boolean.yes') : t('common:boolean.no')}
                      </TierChip>
                    </StyledTd>
                    <StyledTd>
                      {vendor.recertificationDue ? (
                        <TierChip $color="error">{t('vendor:detail.recertification.due', { date: '' }).replace(' — ', '')}</TierChip>
                      ) : vendor.recertificationDueDate ? (
                        <TierChip $color="success">{formatDateToDateBR(vendor.recertificationDueDate)}</TierChip>
                      ) : (
                        <MutedText>{t('vendor:table.na')}</MutedText>
                      )}
                    </StyledTd>
                    <StyledTdCenter>
                      <ActionCell>
                        <ActionIconBtn
                          title={t('common:tooltips.view')}
                          onClick={() => navigate(`/vendors/${vendor._id}`)}
                        >
                          <VisibilitySVG />
                        </ActionIconBtn>
                        <ActionIconBtn
                          title={t('common:tooltips.edit')}
                          onClick={() => onEdit && onEdit(vendor)}
                        >
                          <EditSVG />
                        </ActionIconBtn>
                        <ActionIconBtn
                          $variant="error"
                          title={t('common:tooltips.delete')}
                          onClick={() => handleDelete(vendor._id, vendor.name)}
                        >
                          <DeleteSVG />
                        </ActionIconBtn>
                      </ActionCell>
                    </StyledTdCenter>
                  </StyledTr>
                );
              })
            )}
          </StyledTbody>
        </StyledTable>

        <PaginationRow>
          <CountText>
            {t('vendor:list.count', { count: vendors.length, total })}
          </CountText>
        </PaginationRow>
      </TableCard>
    </Wrapper>
  );
};
